/* eslint-disable react/boolean-prop-naming */
import React, { useState } from "react";
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import { Tabs } from "../../../tabion";
import TabList from "../../../tabion/tabList";
import Tab from "../../../tabion/tab";
import TabPanel from "../../../tabion/tabPanel";
import ButtonComponent from "../../../../atoms/button";
import { constants } from "../../../../../constants/pageConstants/pdpConstants";

const MultiImageUploadInput = dynamic(() => import("../../multiImagePersonalization/multiImageUploadInput"));
const FileUploadInstructions = dynamic(() => import("../../multiImagePersonalization/fileUploadInstructions"));
const MultiTextPersonlization = dynamic(() => import("../../multiTextPersonalization"));

/**
 * ImageAndTextTab renders the tab interface with two tabs: "Upload Photos" and "Enter Text".
 * It manages the state of the selected tab and displays different content depending on the active tab.
 *
 * @component
 * @param {object} props - The props of the component.
 * @param {object} props.item - The item information for rendering the multi-image upload.
 * @param {string} props.productId - The product ID for which the images are uploaded.
 * @param {boolean} props.isMobile - Whether the component is being rendered on a mobile device.
 * @param {array} props.personalizedOptions - Array of personalized options.
 * @param {boolean} props.imageWithText - Flag to determine if the image includes text.
 * @param {boolean} props.isUploadIncomplete - Flag indicating if the image upload process is incomplete.
 * @param {function} props.setIsToastVisible - Function to show/hide toast notification.
 * @param {function} props.setCurrentToast - Function to set the current toast message.
 * @param {function} props.setTextEntered - Function to update the text entered state.
 * @param {function} props.setPreviewImage - Function to set the preview image.
 * @param {function} props.setMultiImageDrawer - Function to set the state of the multi-image drawer.
 * @param {function} props.onContinueMultiImageUpload - Function to handle the continuation of multi-image upload.
 * @returns {JSX.Element} The rendered tab component.
 */
const ImageAndTextTab = ({
  item,
  productId,
  isMobile,
  personalizedOptions,
  imageWithText,
  isUploadIncomplete,
  setIsToastVisible,
  setCurrentToast,
  setTextEntered,
  setPreviewImage,
  setMultiImageDrawer,
  onContinueMultiImageUpload,
}) => {
  const [selectedTab, setSelectedTab] = useState(0); // State for managing selected tab

  /**
   * Handles tab change by updating the selected tab index.
   *
   * @param {number} index - The index of the selected tab.
   */
  const onTabChange = (index) => {
    setSelectedTab(index);
  };

  return (
    <Tabs>
      <TabList
        selected={selectedTab}
        onChange={onTabChange}
        tabCount={2}
        tabListStyle="inline-flex w-full px-20 items-center gap-2 text-center border-b border-[#EEE]"
        isActiveStyle="px-20 py-12 border-t-4 border-t-white-900  border-b-[#4C4D27] font-[500] !text-[#4C4D27]"
      >
        <Tab bgStyleClass="px-20 py-12 w-[175px] border-b-2 font-14 text-center font-500 text-[#757575]">
          {constants.UPLOAD_PHOTOS}
        </Tab>
        <Tab bgStyleClass="px-20 py-12 w-[175px] border-b-2 font-14 text-center font-500 text-[#757575]">
          {constants.ENTER_TEXT}
        </Tab>
      </TabList>

      <TabPanel>
        <div className="h-[calc(100vh-180px)] overflow-auto pb-20 pl-20 pr-20 pt-20">
          <MultiImageUploadInput
            item={item}
            productId={productId}
            setIsToastVisible={setIsToastVisible}
            setCurrentToast={setCurrentToast}
          />
          <FileUploadInstructions item={item} />
        </div>

        <div className="bg-white fixed bottom-0 flex w-full flex-col items-center gap-[10px] border-t border-gray-200 px-[20px] py-[16px]">
          <ButtonComponent
            classes="text-base flex justify-center w-[360px]"
            buttonVariant="filled"
            label="Continue"
            onClick={onContinueMultiImageUpload}
            isRemoveExtraPadding={true}
          />
        </div>
      </TabPanel>

      <TabPanel>
        <MultiTextPersonlization
          isMobile={isMobile}
          productId={productId}
          personalizedOptions={personalizedOptions?.filter((value) => value.contentType === "TEXT")}
          imageWithText={imageWithText}
          isUploadIncomplete={isUploadIncomplete}
          setTextEntered={setTextEntered}
          setPreviewImage={setPreviewImage}
          setMultiImageDrawer={setMultiImageDrawer}
        />
      </TabPanel>
    </Tabs>
  );
};

ImageAndTextTab.propTypes = {
  item: PropTypes.object.isRequired,
  productId: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  personalizedOptions: PropTypes.array,
  imageWithText: PropTypes.bool.isRequired,
  isUploadIncomplete: PropTypes.bool.isRequired,
  setIsToastVisible: PropTypes.func.isRequired,
  setCurrentToast: PropTypes.func.isRequired,
  setTextEntered: PropTypes.func.isRequired,
  setPreviewImage: PropTypes.func.isRequired,
  setMultiImageDrawer: PropTypes.func.isRequired,
  onContinueMultiImageUpload: PropTypes.func.isRequired,
};

export default ImageAndTextTab;
